import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Input,
  Text,
  Card,
  Badge,
  Modal,
} from '@teals/ui-web';
import {
  useGetApplicationQuery,
  useUpdateApplicationMutation,
  useUpdateApplicationAgentsMutation,
  useGetAgentsQuery,
  useGetWebhooksQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useTestWebhookMutation,
  type Webhook,
} from '../../store/api';
import ScopedAnalyticsPanel from '../Analytics/ScopedAnalyticsPanel';

type TabId = 'overview' | 'agents' | 'configuration' | 'webhooks' | 'analytics';

const MODEL_OPTIONS = [
  'gpt-4o',
  'gpt-4o-mini',
  'gpt-4.1-mini',
  'claude-sonnet-4-20250514',
  'claude-3-5-haiku-20241022',
];

const STATUS_MAP: Record<
  string,
  { label: string; variant: 'success' | 'warning' | 'neutral' }
> = {
  active: { label: 'Active', variant: 'success' },
  suspended: { label: 'Suspended', variant: 'warning' },
  retired: { label: 'Retired', variant: 'neutral' },
};

const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

const BackLink = styled.button`
  border: none;
  background: none;
  color: var(--accent);
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  border-bottom: 1px solid var(--border);
  margin-bottom: 1.5rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: none;
  background: none;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) => (p.$active ? 'var(--accent)' : 'var(--text-muted)')};
  border-bottom: 2px solid
    ${(p) => (p.$active ? 'var(--accent)' : 'transparent')};
  cursor: pointer;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th,
  td {
    text-align: left;
    padding: 0.75rem;
    border-bottom: 1px solid var(--border);
    vertical-align: top;
  }

  th {
    color: var(--text-muted);
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const Mono = styled.code`
  font-family: monospace;
  font-size: 0.75rem;
  word-break: break-all;
`;

const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Callout = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: var(--surface-muted, #f5f5f5);
  color: var(--text-muted);
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const SecretBox = styled.div`
  padding: 0.75rem;
  border: 1px dashed var(--border);
  border-radius: 8px;
  background: var(--surface, #fff);
  font-family: monospace;
  font-size: 0.8rem;
  word-break: break-all;
`;

const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: var(--text-muted);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.375rem;
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-primary);
  word-break: break-all;
`;

const AgentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const ApplicationDetail: React.FC = () => {
  const { id: appId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabId) || 'overview';
  const [tab, setTab] = useState<TabId>(
    ['overview', 'agents', 'configuration', 'webhooks', 'analytics'].includes(
      initialTab,
    )
      ? initialTab
      : 'overview',
  );

  const handleTabChange = (next: TabId) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const { data: app, isLoading, isError } = useGetApplicationQuery(appId, {
    skip: !appId,
  });
  const [updateApplication] = useUpdateApplicationMutation();
  const [updateApplicationAgents] = useUpdateApplicationAgentsMutation();
  const { data: allAgents = [] } = useGetAgentsQuery();

  const {
    data: webhooks = [],
    isLoading: webhooksLoading,
    isError: webhooksError,
  } = useGetWebhooksQuery(appId, { skip: !appId || tab !== 'webhooks' });
  const [createWebhook, { isLoading: isCreating }] = useCreateWebhookMutation();
  const [updateWebhook] = useUpdateWebhookMutation();
  const [deleteWebhook] = useDeleteWebhookMutation();
  const [testWebhook] = useTestWebhookMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [editWebhook, setEditWebhook] = useState<Webhook | null>(null);
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [eventsText, setEventsText] = useState('');
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const [cfgName, setCfgName] = useState('');
  const [cfgDesc, setCfgDesc] = useState('');
  const [allowedModels, setAllowedModels] = useState<string[]>([]);
  const [budgetLimit, setBudgetLimit] = useState('');
  const [budgetAlert, setBudgetAlert] = useState('80');
  const [addAgentId, setAddAgentId] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);

  React.useEffect(() => {
    if (!app) return;
    setCfgName(app.name || '');
    setCfgDesc(app.description || '');
    setAllowedModels(
      Array.isArray(app.allowedModels) ? [...app.allowedModels] : [],
    );
    const budget = (app.budgetConfig || {}) as Record<string, unknown>;
    setBudgetLimit(
      budget.token_cap != null
        ? String(budget.token_cap)
        : budget.monthly_limit != null
          ? String(budget.monthly_limit)
          : '',
    );
    setBudgetAlert(
      budget.alert_threshold_pct != null
        ? String(budget.alert_threshold_pct)
        : budget.alert_threshold != null
          ? String(budget.alert_threshold)
          : '80',
    );
  }, [app]);

  const associatedAgents = useMemo(() => {
    const ids = new Set(app?.agentIds || []);
    return allAgents.filter((a) => ids.has(a.id));
  }, [app?.agentIds, allAgents]);

  const availableAgents = useMemo(() => {
    const ids = new Set(app?.agentIds || []);
    return allAgents.filter((a) => !ids.has(a.id));
  }, [app?.agentIds, allAgents]);

  const status = useMemo(() => {
    if (!app) return STATUS_MAP.active;
    return STATUS_MAP[app.status] || STATUS_MAP.active;
  }, [app]);

  const toggleStatus = async () => {
    if (!app || app.status === 'retired') return;
    const next = app.status === 'active' ? 'suspended' : 'active';
    await updateApplication({ appId: app.appId, body: { status: next } });
  };

  const handleAddAgent = async () => {
    if (!app || !addAgentId) return;
    const next = [...(app.agentIds || []), addAgentId];
    await updateApplicationAgents({ appId: app.appId, agentIds: next });
    setAddAgentId('');
  };

  const handleRemoveAgent = async (agentId: string) => {
    if (!app) return;
    const next = (app.agentIds || []).filter((id) => id !== agentId);
    await updateApplicationAgents({ appId: app.appId, agentIds: next });
  };

  const handleSaveConfig = async () => {
    if (!app) return;
    setSavingConfig(true);
    try {
      await updateApplication({
        appId: app.appId,
        body: {
          name: cfgName.trim(),
          description: cfgDesc.trim() || undefined,
          allowed_models: allowedModels,
          budget_config: {
            token_cap: budgetLimit ? Number(budgetLimit) : null,
            alert_threshold_pct: budgetAlert ? Number(budgetAlert) : 80,
            period: 'monthly',
          },
        },
      }).unwrap();
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleModel = (model: string) => {
    setAllowedModels((prev) =>
      prev.includes(model) ? prev.filter((m) => m !== model) : [...prev, model],
    );
  };

  const resetCreateForm = () => {
    setUrl('');
    setDescription('');
    setEventsText('');
  };

  const handleCreate = async () => {
    if (!url.trim()) return;
    const events = eventsText
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    try {
      const created = await createWebhook({
        appId,
        body: {
          url: url.trim(),
          description: description.trim() || undefined,
          events: events.length ? events : undefined,
        },
      }).unwrap();
      setCreatedSecret(created.signingSecret || null);
      resetCreateForm();
      setCreateOpen(false);
    } catch {
      // keep modal open
    }
  };

  const handleEditSave = async () => {
    if (!editWebhook || !url.trim()) return;
    const events = eventsText
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    try {
      await updateWebhook({
        appId,
        webhookId: editWebhook.webhookId,
        body: {
          url: url.trim(),
          description: description.trim() || undefined,
          events: events.length ? events : undefined,
        },
      }).unwrap();
      setEditWebhook(null);
      resetCreateForm();
    } catch {
      // keep open
    }
  };

  const openEdit = (wh: Webhook) => {
    setEditWebhook(wh);
    setUrl(wh.url);
    setDescription(wh.description || '');
    setEventsText((wh.events || []).join(', '));
  };

  const handleToggleEnabled = async (wh: Webhook) => {
    await updateWebhook({
      appId,
      webhookId: wh.webhookId,
      body: { enabled: !wh.enabled },
    });
  };

  const handleRotate = async (wh: Webhook) => {
    try {
      const updated = await updateWebhook({
        appId,
        webhookId: wh.webhookId,
        body: { rotate_secret: true },
      }).unwrap();
      if (updated.signingSecret) {
        setCreatedSecret(updated.signingSecret);
      }
    } catch {
      // ignore
    }
  };

  const handleDelete = async (wh: Webhook) => {
    if (!window.confirm(`Delete webhook ${wh.webhookId}?`)) return;
    await deleteWebhook({ appId, webhookId: wh.webhookId });
  };

  const handleTest = async (wh: Webhook) => {
    setTestMessage(null);
    try {
      const result = await testWebhook({
        appId,
        webhookId: wh.webhookId,
      }).unwrap();
      setTestMessage(
        result.delivered
          ? `Test delivered to ${wh.webhookId}`
          : `Test failed for ${wh.webhookId}`,
      );
    } catch {
      setTestMessage(`Test failed for ${wh.webhookId}`);
    }
  };

  if (isLoading) {
    return (
      <PageWrapper>
        <EmptyState>Loading application…</EmptyState>
      </PageWrapper>
    );
  }

  if (isError || !app) {
    return (
      <PageWrapper>
        <BackLink onClick={() => navigate('/applications')}>
          ← Back to Applications
        </BackLink>
        <EmptyState>Application not found.</EmptyState>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <BackLink onClick={() => navigate('/applications')}>
        ← Back to Applications
      </BackLink>

      <Header>
        <div>
          <Text variant="h2">{app.name}</Text>
          <Text
            variant="body1"
            style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}
          >
            <Mono>{app.appId}</Mono>
          </Text>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Badge variant={status.variant} size="sm">
            {status.label}
          </Badge>
          {app.status !== 'retired' && (
            <Button variant="secondary" size="sm" onClick={toggleStatus}>
              {app.status === 'active' ? 'Suspend' : 'Reactivate'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleTabChange('analytics')}
          >
            View Analytics
          </Button>
        </div>
      </Header>

      <Tabs>
        <TabButton
          $active={tab === 'overview'}
          onClick={() => handleTabChange('overview')}
        >
          Overview
        </TabButton>
        <TabButton
          $active={tab === 'agents'}
          onClick={() => handleTabChange('agents')}
        >
          Agents
        </TabButton>
        <TabButton
          $active={tab === 'configuration'}
          onClick={() => handleTabChange('configuration')}
        >
          Configuration
        </TabButton>
        <TabButton
          $active={tab === 'webhooks'}
          onClick={() => handleTabChange('webhooks')}
        >
          Webhooks
        </TabButton>
        <TabButton
          $active={tab === 'analytics'}
          onClick={() => handleTabChange('analytics')}
        >
          Analytics
        </TabButton>
      </Tabs>

      {tab === 'overview' && (
        <Card padding={20} elevation="sm">
          <DetailGrid>
            <div>
              <DetailLabel>Description</DetailLabel>
              <DetailValue>{app.description || 'No description'}</DetailValue>
            </div>
            <div>
              <DetailLabel>Organisation</DetailLabel>
              <DetailValue>{app.orgId}</DetailValue>
            </div>
            <div>
              <DetailLabel>Agents</DetailLabel>
              <DetailValue>
                {app.agentCount ?? app.agentIds?.length ?? 0} associated
              </DetailValue>
            </div>
            <div>
              <DetailLabel>Budget</DetailLabel>
              <DetailValue>
                {app.budgetConfig &&
                (app.budgetConfig as Record<string, unknown>).monthly_limit !=
                  null
                  ? `Limit ${(app.budgetConfig as Record<string, unknown>).monthly_limit}`
                  : (app.budgetConfig as Record<string, unknown>)?.token_cap !=
                      null
                    ? `Limit ${(app.budgetConfig as Record<string, unknown>).token_cap} tokens`
                    : 'Not configured'}
              </DetailValue>
            </div>
            <div>
              <DetailLabel>Created</DetailLabel>
              <DetailValue>{app.createdAt || '—'}</DetailValue>
            </div>
            <div>
              <DetailLabel>API Key Profiles</DetailLabel>
              <DetailValue>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/business')}
                >
                  Manage in Business Center
                </Button>
              </DetailValue>
            </div>
          </DetailGrid>
        </Card>
      )}

      {tab === 'agents' && (
        <Card padding={20} elevation="sm">
          <SectionHeader>
            <Text variant="h4">Associated agents</Text>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                value={addAgentId}
                onChange={(e) => setAddAgentId(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="">Select agent…</option>
                {availableAgents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddAgent}
                disabled={!addAgentId}
              >
                + Add Agent
              </Button>
            </div>
          </SectionHeader>
          {associatedAgents.length === 0 ? (
            <EmptyState>No agents associated yet.</EmptyState>
          ) : (
            associatedAgents.map((agent) => (
              <AgentRow key={agent.id}>
                <div>
                  <Text variant="body1" style={{ margin: 0 }}>
                    {agent.name}
                  </Text>
                  <Text
                    variant="body1"
                    style={{
                      margin: 0,
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {agent.versionStatus} · {agent.scope}
                  </Text>
                </div>
                <RowActions>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      navigate(`/agents/${encodeURIComponent(agent.id)}`)
                    }
                  >
                    Open
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAgent(agent.id)}
                  >
                    Remove
                  </Button>
                </RowActions>
              </AgentRow>
            ))
          )}
        </Card>
      )}

      {tab === 'configuration' && (
        <Card padding={20} elevation="sm">
          <ModalForm>
            <Input
              label="Application name"
              value={cfgName}
              onChange={(e) => setCfgName(e.target.value)}
            />
            <Input
              label="Description"
              value={cfgDesc}
              onChange={(e) => setCfgDesc(e.target.value)}
            />
            <div>
              <DetailLabel>Allowed models</DetailLabel>
              <CheckboxList>
                {MODEL_OPTIONS.map((model) => (
                  <CheckboxLabel key={model}>
                    <input
                      type="checkbox"
                      checked={allowedModels.includes(model)}
                      onChange={() => toggleModel(model)}
                    />
                    {model}
                  </CheckboxLabel>
                ))}
              </CheckboxList>
            </div>
            <Input
              label="Monthly token budget (token_cap)"
              value={budgetLimit}
              onChange={(e) => setBudgetLimit(e.target.value)}
              placeholder="e.g. 500"
            />
            <Input
              label="Alert threshold (%)"
              value={budgetAlert}
              onChange={(e) => setBudgetAlert(e.target.value)}
              placeholder="80"
            />
            <ModalActions>
              <Button
                variant="primary"
                onClick={handleSaveConfig}
                disabled={!cfgName.trim() || savingConfig}
              >
                {savingConfig ? 'Saving…' : 'Save configuration'}
              </Button>
            </ModalActions>
          </ModalForm>
        </Card>
      )}

      {tab === 'webhooks' && (
        <>
          <Callout>
            Webhooks are application-scoped. All agents associated with this
            application deliver events to these endpoints.
          </Callout>

          <SectionHeader>
            <Text variant="h4">Registered endpoints</Text>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Add webhook
            </Button>
          </SectionHeader>

          {createdSecret && (
            <Callout>
              <strong>Signing secret (shown once):</strong>
              <SecretBox style={{ marginTop: '0.5rem' }}>{createdSecret}</SecretBox>
              <Button
                variant="ghost"
                size="sm"
                style={{ marginTop: '0.5rem' }}
                onClick={() => setCreatedSecret(null)}
              >
                Dismiss
              </Button>
            </Callout>
          )}

          {testMessage && (
            <Callout>
              {testMessage}
              <Button
                variant="ghost"
                size="sm"
                style={{ marginLeft: '0.5rem' }}
                onClick={() => setTestMessage(null)}
              >
                Dismiss
              </Button>
            </Callout>
          )}

          {webhooksLoading && <EmptyState>Loading webhooks…</EmptyState>}
          {webhooksError && (
            <EmptyState>Failed to load webhooks for this application.</EmptyState>
          )}
          {!webhooksLoading && !webhooksError && webhooks.length === 0 && (
            <EmptyState>
              No webhooks yet. Add an HTTPS endpoint to receive pipeline events.
            </EmptyState>
          )}

          {webhooks.length > 0 && (
            <Card padding={0} elevation="sm">
              <Table>
                <thead>
                  <tr>
                    <th>URL</th>
                    <th>Events</th>
                    <th>Status</th>
                    <th>Failures</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {webhooks.map((wh) => (
                    <tr key={wh.webhookId}>
                      <td>
                        <div>{wh.description || '—'}</div>
                        <Mono>{wh.url}</Mono>
                        <div>
                          <Mono>{wh.webhookId}</Mono>
                        </div>
                      </td>
                      <td>
                        {wh.events && wh.events.length
                          ? wh.events.join(', ')
                          : 'All events'}
                      </td>
                      <td>
                        <Badge
                          variant={wh.enabled ? 'success' : 'neutral'}
                          size="sm"
                        >
                          {wh.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </td>
                      <td>{wh.consecutiveFailures ?? 0}</td>
                      <td>
                        <RowActions>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(wh)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleEnabled(wh)}
                          >
                            {wh.enabled ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTest(wh)}
                          >
                            Test
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRotate(wh)}
                          >
                            Rotate secret
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(wh)}
                          >
                            Delete
                          </Button>
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </>
      )}

      {tab === 'analytics' && (
        <ScopedAnalyticsPanel
          scope="application"
          scopeLabel={app.name}
          applicationId={app.appId || appId}
          onOpenConfiguration={() => handleTabChange('configuration')}
        />
      )}

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add webhook"
      >
        <ModalForm>
          <Input
            label="HTTPS URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-app.example.com/webhooks/lumina"
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
          <Input
            label="Events (comma-separated, blank = all)"
            value={eventsText}
            onChange={(e) => setEventsText(e.target.value)}
            placeholder="conversation.result, job.request"
          />
          <ModalActions>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!url.trim() || isCreating}
            >
              {isCreating ? 'Creating…' : 'Create'}
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>

      <Modal
        isOpen={!!editWebhook}
        onClose={() => {
          setEditWebhook(null);
          resetCreateForm();
        }}
        title="Edit webhook"
      >
        <ModalForm>
          <Input
            label="HTTPS URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Input
            label="Events (comma-separated, blank = all)"
            value={eventsText}
            onChange={(e) => setEventsText(e.target.value)}
          />
          <ModalActions>
            <Button
              variant="ghost"
              onClick={() => {
                setEditWebhook(null);
                resetCreateForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleEditSave}
              disabled={!url.trim()}
            >
              Save
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>
    </PageWrapper>
  );
};

export default ApplicationDetail;
